import { createGeminiImage, isGeminiAvailable } from './gemini-client.js';

const form = document.querySelector('form[data-wallpaper-form]');
const deviceSelect = document.querySelector('#wallpaper-device');
const vibeSelect = document.querySelector('#wallpaper-vibe');
const motifSelect = document.querySelector('#wallpaper-motif');
const notesInput = document.querySelector('#wallpaper-notes');
const statusEl = document.querySelector('[data-status]');
const previewFrame = document.querySelector('[data-wallpaper-preview]');
const previewLabel = document.querySelector('[data-preview-label]');
const downloadBtn = document.querySelector('[data-download]');
const resetBtn = document.querySelector('[data-reset]');
const submitBtn = form?.querySelector('button[type="submit"]');

const DEFAULT_STATUS = 'Choose a device, palette, and motif to start.';
let geminiReady = true;

const DEVICE_PRESETS = {
  phone: {
    prompt:
      'Deliver a vertical wallpaper sized 1242x2688 (9:16). Keep the main rabbits centered with generous breathing room for lock screen text.',
    label: 'Phone canvas — 9:16 ratio',
    ratioClass: null
  },
  tablet: {
    prompt:
      'Deliver a portrait wallpaper sized 2048x2732 (3:4). Fill the edges with repeating motifs while keeping a central vignette.',
    label: 'Tablet canvas — 3:4 ratio',
    ratioClass: 'ratio-tablet'
  },
  desktop: {
    prompt:
      'Deliver a landscape wallpaper sized 2560x1440 (16:9). Build a panoramic scene with layered foreground, midground, and sky.',
    label: 'Desktop canvas — 16:9 ratio',
    ratioClass: 'ratio-desktop'
  }
};

const VIBE_PROMPTS = {
  sunrise: 'Palette: croissant dough beige, apricot glaze, rosewater pink, and brushed gold. Lighting: buttery sunrise bloom, soft film grain.',
  twilight: 'Palette: plum velvet, indigo, cobalt, and amber sparks. Lighting: cinematic twilight with rim lights and floating bokeh.',
  garden: 'Palette: moss, eucalyptus, pistachio, sage, and blush petals. Lighting: greenhouse dappled sun with soft shadows.',
  moonlit: 'Palette: lapis, midnight navy, parchment cream, and candle brass. Lighting: warm moonbeams and library glow.'
};

const MOTIF_PROMPTS = {
  pastry: 'Motifs: croissant stacks, piping bags, marble pastry tables, copper whisks, swirling steam, sprinkled sugar.',
  travel: 'Motifs: vintage trams, ticket stubs, passport stamps, stacked suitcases, travel diaries, scenic windows.',
  flora: 'Motifs: cascading wisteria, camellia clusters, herb bundles, ikebana frogs, botanical labels.',
  night: 'Motifs: lantern ribbons, firefly jars, paper fans, festival banners, sparkler trails, midnight confetti.'
};

function buildPrompt(deviceKey, vibeKey, motifKey, notes = '') {
  const device = DEVICE_PRESETS[deviceKey] || DEVICE_PRESETS.phone;
  const vibe = VIBE_PROMPTS[vibeKey] || VIBE_PROMPTS.sunrise;
  const motif = MOTIF_PROMPTS[motifKey] || MOTIF_PROMPTS.pastry;
  const trimmedNotes = notes.trim();

  return `Design a Croissant Bunny wallpaper with gentle Dutch rabbits, soft gouache shading, clean outlines, and subtle paper grain. ${device.prompt} ${vibe} ${motif} Include repeating stickers, floating charms, and tiny serif labels that feel bespoke. Keep edges tidy with mild vignette.${
    trimmedNotes ? ` Custom cue: ${trimmedNotes}.` : ''
  }`;
}

function setStatus(message, isError = false) {
  if (!statusEl) return;
  statusEl.textContent = message;
  statusEl.classList.toggle('error', Boolean(isError));
}

function setPreview(base64) {
  if (!previewFrame) return;
  if (!base64) {
    previewFrame.style.backgroundImage = '';
    previewFrame.classList.remove('has-image');
    previewFrame.classList.add('placeholder');
    if (downloadBtn) {
      downloadBtn.href = 'javascript:void(0)';
      downloadBtn.classList.add('is-hidden');
      downloadBtn.setAttribute('aria-disabled', 'true');
      downloadBtn.setAttribute('tabindex', '-1');
    }
    return;
  }

  const dataUrl = `data:image/png;base64,${base64}`;
  previewFrame.style.backgroundImage = `url(${dataUrl})`;
  previewFrame.classList.add('has-image');
  previewFrame.classList.remove('placeholder');
  if (downloadBtn) {
    downloadBtn.href = dataUrl;
    downloadBtn.classList.remove('is-hidden');
    downloadBtn.removeAttribute('aria-disabled');
    downloadBtn.removeAttribute('tabindex');
  }
}

async function initGeminiAvailability() {
  if (!form) return;
  geminiReady = await isGeminiAvailable();
  if (!geminiReady) {
    submitBtn?.setAttribute('disabled', 'true');
    form.classList.add('tool-disabled');
    setStatus('Image generation is offline. Please come back soon.');
  }
}

function updatePreviewAspect(deviceKey) {
  if (!previewFrame) return;
  previewFrame.classList.remove('ratio-tablet', 'ratio-desktop');
  const preset = DEVICE_PRESETS[deviceKey] || DEVICE_PRESETS.phone;
  if (preset.ratioClass) {
    previewFrame.classList.add(preset.ratioClass);
  }
  if (previewLabel) {
    previewLabel.textContent = preset.label;
  }
}

function handleReset() {
  form?.reset();
  updatePreviewAspect(deviceSelect?.value || 'phone');
  setPreview('');
  setStatus(DEFAULT_STATUS);
}

if (form && deviceSelect && vibeSelect && motifSelect && previewFrame && statusEl) {
  handleReset();

  deviceSelect.addEventListener('change', () => {
    updatePreviewAspect(deviceSelect.value);
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!geminiReady) {
      setStatus('Image generation is offline. Try again later.', true);
      return;
    }

    const device = deviceSelect.value;
    const vibe = vibeSelect.value;
    const motif = motifSelect.value;
    const notes = notesInput?.value || '';
    const prompt = buildPrompt(device, vibe, motif, notes);

    try {
      setStatus('Composing your wallpaper — this may take a few seconds…');
      setPreview('');
      const output = await createGeminiImage({ prompt });
      setPreview(output);
      setStatus('Wallpaper ready! Download the PNG or adjust the controls.');
    } catch (error) {
      console.error(error);
      setStatus(error.message || 'Generation failed. Please try again.', true);
      setPreview('');
    }
  });

  resetBtn?.addEventListener('click', handleReset);
  initGeminiAvailability();
}
